import { useQuery } from '@tanstack/react-query';
import { privateApi } from '../../../api/api';
import { AxiosRequestConfig } from 'axios';

// Impor skema dan tipe yang relevan
import {
    GetUsersV4Payload,
    GetUsersV4Response,
    GetUsersV4PayloadSchema,
    GetUsersV4ResponseSchema
} from '../schemas/GetUsersV4Schema';

const API_ENDPOINT = '/v4/user';

/**
 * Fungsi helper murni untuk mengubah objek payload menjadi objek parameter
 * yang sesuai dengan format yang diharapkan oleh Axios.
 * Laravel/PHP mengharapkan parameter array diformat sebagai `filter[key]=value`.
 * 
 * @param params - Objek payload dari hook.
 * @returns Objek yang siap digunakan dalam opsi `params` Axios.
 */
const buildQueryParams = (params: GetUsersV4Payload): AxiosRequestConfig['params'] => {
    const queryParams: Record<string, any> = {};

    // Handle filter: mengubah { filter: { name: 'test' } } menjadi { 'filter[name]': 'test' }
    if (params.filter) {
        for (const key in params.filter) {
            // Memastikan hanya properti milik objek itu sendiri yang diproses
            if (Object.prototype.hasOwnProperty.call(params.filter, key)) {
                // Hanya tambahkan ke params jika nilainya ada dan tidak kosong
                const filterValue = params.filter[key as keyof typeof params.filter];
                if (filterValue) {
                    queryParams[`filter[${key}]`] = filterValue;
                }
            }
        }
    }

    // Handle parameter tingkat atas lainnya
    // `search` dikirim apa adanya (bukan dibungkus `filter[...]`) — backend
    // memperlakukannya sebagai pencarian gabungan name/email/phone.
    if (params.search) queryParams.search = params.search;
    if (params.include) queryParams.include = params.include;
    if (params.orderBy) queryParams.orderBy = params.orderBy;
    if (params.sort) queryParams.sort = params.sort;
    if (params.limit) queryParams.limit = params.limit;
    if (params.page) queryParams.page = params.page;
    if (params.all) queryParams.all = params.all;

    return queryParams;
};

/**
 * Hook kustom untuk mengambil daftar pengguna dengan paginasi dan filter (Versi 4).
 * Menggunakan metode GET dengan query parameters.
 * 
 * @param payload - Objek yang berisi filter, paginasi, sorting, dan include.
 * @returns Hasil dari React Query, termasuk data, status loading, dan error.
 */
export const useGetUsersV4 = (payload: GetUsersV4Payload) => {

    // Validasi payload input menggunakan Zod.
    const validation = GetUsersV4PayloadSchema.safeParse(payload);

    // Dapatkan payload yang valid atau objek kosong jika tidak valid.
    // Ini digunakan untuk stabilitas queryKey.
    const validPayload = validation.success ? validation.data : {};

    return useQuery<GetUsersV4Response, Error>({
        /**
         * queryKey harus secara unik merepresentasikan request ini.
         * Dengan menyertakan `validPayload`, setiap perubahan pada filter atau paginasi
         * akan membuat queryKey baru, yang memicu refetch.
         */
        queryKey: ['users', 'v4', 'list', validPayload],

        /**
         * Fungsi yang akan dieksekusi oleh React Query untuk mengambil data.
         */
        queryFn: async (): Promise<GetUsersV4Response> => {
            // Meskipun sudah ada `enabled`, pengecekan di sini memberikan lapisan keamanan tambahan.
            if (!validation.success) {
                console.error("Query function was called with invalid payload:", validation.error.flatten());
                throw new Error("Invalid payload structure for useGetUsersV4.");
            }

            // Ubah payload yang sudah valid menjadi query parameters
            const params = buildQueryParams(validation.data);

            try {
                // Melakukan request GET dengan `params`
                const response = await privateApi.get(API_ENDPOINT, { params });

                // Validasi respons dari server
                const responseValidation = GetUsersV4ResponseSchema.safeParse(response.data);
                if (!responseValidation.success) {
                    console.error("API response validation failed in useGetUsersV4:", responseValidation.error.flatten());
                    throw new Error("Invalid data format received from the server.");
                }

                return responseValidation.data;
            } catch (error) {
                console.error(`Failed to fetch users (V4):`, error);
                throw error;
            }
        },

        /**
         * Query hanya akan berjalan jika payload awal yang diberikan ke hook valid.
         * Ini mencegah request yang tidak perlu jika ada kesalahan pemrograman di komponen pemanggil.
         */
        enabled: validation.success,

        // Opsi standar untuk React Query
        refetchOnWindowFocus: false,
        retry: 1,
    });
};